import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { dataFechamentoAutomatico, janelaAindaAberta, algumMangaraVencido } from "@shared/ciclo-fechamento";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { useVariedades } from "@/hooks/use-variedades-cacho";
import { useToleranciaDias } from "@/hooks/use-tolerancia";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, 
  Calendar, 
  TrendingUp, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  AlertCircle,
  Package,
  Target,
  Activity,
  Lock,
  Unlock
} from "lucide-react";
import { useLocation } from "wouter";
import { useNavegacao } from "@/lib/navegacao";
import type { Ciclo, MangaraWithRelations, ColheitaWithRelations } from "@shared/schema";
import { useSetores } from "@/hooks/use-setores";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import { RegistrarPerdaDialog } from "@/components/registrar-perda-dialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { useMemo } from "react";

import { PageHeader } from "@/components/layout/page-header";
// Helper para calcular semana ISO
function getIsoWeek(date: Date): { week: number; year: number } {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayNum = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - dayNum);
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { week: week, year: d.getFullYear() };
}

// Helper para calcular dias pós corte de uma colheita
// Encontra o mangará mais recente (data corte <= data colheita) do mesmo setor/cor/ciclo
function getDiasPosCorte(
  colheita: ColheitaWithRelations,
  cicloMangaras: MangaraWithRelations[]
): number | null {
  if (!colheita.cicloId || !colheita.corFitaId) {
    return null; // Sem plano
  }

  // Filtrar mangarás correspondentes (mesmo setor, cor e ciclo)
  const mangarasCorrespondentes = cicloMangaras.filter(m =>
    m.setorId === colheita.setorId &&
    m.corFitaId === colheita.corFitaId &&
    m.cicloId === colheita.cicloId
  );

  if (mangarasCorrespondentes.length === 0) {
    return null;
  }

  const dataColheita = new Date(colheita.dataColheita);
  dataColheita.setHours(0, 0, 0, 0);

  // Encontrar mangará mais recente com data de corte <= data colheita
  const mangarasValidos = mangarasCorrespondentes.filter(m => {
    const dataCorte = new Date(m.dataCorteMangara);
    dataCorte.setHours(0, 0, 0, 0);
    return dataCorte <= dataColheita;
  });

  if (mangarasValidos.length === 0) {
    // Se não houver mangarás com corte <= colheita, retornar null (sem plano válido)
    return null;
  }

  // Ordenar por data de corte (mais recente primeiro) e pegar o primeiro
  const mangaraMaisRecente = mangarasValidos.reduce((latest, current) => {
    return new Date(current.dataCorteMangara) > new Date(latest.dataCorteMangara) ? current : latest;
  });

  const dataCorte = new Date(mangaraMaisRecente.dataCorteMangara);
  dataCorte.setHours(0, 0, 0, 0);

  // Calcular diferença em dias
  const diasDiff = Math.floor((dataColheita.getTime() - dataCorte.getTime()) / (1000 * 60 * 60 * 24));

  return diasDiff;
}

// Helper para calcular estatísticas de dias pós corte
interface HarvestStats {
  mediana: number;
  previsaoMedia: number;
  realizadoMedia: number;
  semanaPrevisao: number;
  semanaRealizado: number;
  diferencaMedia: number;
  p25: number;
  p75: number;
  p90: number;
  totalRegistros: number;
}

function calcularEstatisticasColheita(
  colheitas: ColheitaWithRelations[],
  mangaras: MangaraWithRelations[]
): HarvestStats | null {
  const diasArray: number[] = [];

  for (const colheita of colheitas) {
    const dias = getDiasPosCorte(colheita, mangaras);
    if (dias !== null) {
      diasArray.push(dias);
    }
  }

  if (diasArray.length === 0) {
    return null;
  }

  // Ordenar para cálculo de percentis
  const sorted = [...diasArray].sort((a, b) => a - b);

  // Calcular percentis
  const getPercentile = (arr: number[], p: number) => {
    const index = (p / 100) * (arr.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    if (lower === upper) return arr[lower];
    return arr[lower] * (1 - weight) + arr[upper] * weight;
  };

  const mediana = getPercentile(sorted, 50);
  const p25 = getPercentile(sorted, 25);
  const p75 = getPercentile(sorted, 75);
  const p90 = getPercentile(sorted, 90);

  // Calcular médias ponderadas (ambas por quantidade de cachos)
  // Usar a mesma lógica de getDiasPosCorte para selecionar um único mangará por colheita
  let somaPrevisaoPonderada = 0;
  let somaRealizadoPonderada = 0;
  let somaDiferencaPonderada = 0;
  let somaPesos = 0; // soma de quantidades para ponderação

  for (const colheita of colheitas) {
    if (!colheita.cicloId || !colheita.corFitaId) continue;

    const mangarasCorrespondentes = mangaras.filter(m =>
      m.setorId === colheita.setorId &&
      m.corFitaId === colheita.corFitaId &&
      m.cicloId === colheita.cicloId
    );

    if (mangarasCorrespondentes.length === 0) continue;

    const dataColheita = new Date(colheita.dataColheita);
    dataColheita.setHours(0, 0, 0, 0);

    // Encontrar mangará mais recente com data de corte <= data colheita (mesma lógica de getDiasPosCorte)
    const mangarasValidos = mangarasCorrespondentes.filter(m => {
      const dataCorte = new Date(m.dataCorteMangara);
      dataCorte.setHours(0, 0, 0, 0);
      return dataCorte <= dataColheita;
    });

    if (mangarasValidos.length === 0) continue; // Sem mangará válido, pular

    const mangaraMaisRecente = mangarasValidos.reduce((latest, current) => {
      return new Date(current.dataCorteMangara) > new Date(latest.dataCorteMangara) ? current : latest;
    });

    const dataCorte = new Date(mangaraMaisRecente.dataCorteMangara);
    dataCorte.setHours(0, 0, 0, 0);
    const dataPrevista = new Date(mangaraMaisRecente.dataPrevistaColheita);
    dataPrevista.setHours(0, 0, 0, 0);

    const diasPrevistos = Math.floor((dataPrevista.getTime() - dataCorte.getTime()) / (1000 * 60 * 60 * 24));
    const diasReais = Math.floor((dataColheita.getTime() - dataCorte.getTime()) / (1000 * 60 * 60 * 24));
    const peso = colheita.quantidadeCachos || 0;

    // Skip harvests with no quantity (consistent with Performance por Setor)
    if (peso <= 0) continue;

    somaPrevisaoPonderada += diasPrevistos * peso;
    somaRealizadoPonderada += diasReais * peso;
    somaDiferencaPonderada += (diasReais - diasPrevistos) * peso;
    somaPesos += peso;
  }

  const previsaoMedia = somaPesos > 0 ? somaPrevisaoPonderada / somaPesos : 0;
  const realizadoMedia = somaPesos > 0 ? somaRealizadoPonderada / somaPesos : 0;
  const diferencaMedia = somaPesos > 0 ? somaDiferencaPonderada / somaPesos : 0;
  
  // Calcular semanas (dividir por 7 e arredondar)
  const semanaPrevisao = Math.round(previsaoMedia / 7);
  const semanaRealizado = Math.round(realizadoMedia / 7);

  return {
    mediana: Math.round(mediana * 10) / 10,
    previsaoMedia: Math.round(previsaoMedia * 10) / 10,
    realizadoMedia: Math.round(realizadoMedia * 10) / 10,
    semanaPrevisao,
    semanaRealizado,
    diferencaMedia: Math.round(diferencaMedia * 10) / 10,
    p25: Math.round(p25 * 10) / 10,
    p75: Math.round(p75 * 10) / 10,
    p90: Math.round(p90 * 10) / 10,
    totalRegistros: diasArray.length,
  };
}

export default function AnaliseCicloPage() {
  const [, params] = useRoute("/cacho/ciclos/:id");
  const [, setLocation] = useLocation();
  // `?back=` morreu: a origem agora vai no history.state, e `voltar()` sabe
  // distinguir "vim de uma lista nossa" de "abri por link direto".
  const { voltar } = useNavegacao();
  // PR 2: fechar/reativar ciclo é `cacho.fitas.fechar` (ADMIN e GESTOR).
  const isAdmin = usePode("cacho.fitas.fechar");
  const { toast } = useToast();
  const [isFecharDialogOpen, setIsFecharDialogOpen] = useState(false);
  const [isReativarDialogOpen, setIsReativarDialogOpen] = useState(false);
  const [motivoPerdaId, setMotivoPerdaId] = useState<string>("");
  const [distribuirPerdas, setDistribuirPerdas] = useState(false);
  const [forcarFechamento, setForcarFechamento] = useState(false);
  const cicloId = params?.id;

  const { data: ciclo, isLoading: cicloLoading } = useQuery<Ciclo>({
    queryKey: ["/api/ciclos", cicloId],
    enabled: !!cicloId,
  });

  const { data: mangaras, isLoading: mangarasLoading } = useQuery<MangaraWithRelations[]>({
    queryKey: ["/api/mangaras"],
  });

  const { data: colheitas, isLoading: colheitasLoading } = useQuery<ColheitaWithRelations[]>({
    queryKey: ["/api/colheitas"],
  });

  const { data: cores } = useQuery<any[]>({
    queryKey: ["/api/cores"],
  });

  const { data: motivosPerdas, refetch: refetchMotivos } = useQuery<any[]>({
    queryKey: ["/api/motivos-perdas"],
  });

  // Lookup do nome da variedade DO CICLO — não há seletor de variedade aqui, e
  // ciclo é sempre de banana (mangará só nasce de setor de banana).
  const { data: variedades } = useVariedades();

  const { setores } = useSetores({ somenteProdutivos: true });

  const cicloMangaras = mangaras?.filter(m => m.cicloId === cicloId) || [];
  const cicloVariedade = variedades?.find((v: any) => v.id === ciclo?.variedadeId);
  
  // Filter colheitas to show only from sectors with matching variety, ordered by date
  const cicloColheitas = useMemo(() => {
    if (!colheitas || !setores || !ciclo) return [];
    
    const setoresComVariedade = setores
      .filter(s => s.variedadeId === ciclo.variedadeId)
      .map(s => s.id);
    
    return colheitas
      .filter(c => c.cicloId === cicloId && setoresComVariedade.includes(c.setorId))
      .sort((a, b) => {
        const dateA = a.dataColheita ? new Date(a.dataColheita).getTime() : 0;
        const dateB = b.dataColheita ? new Date(b.dataColheita).getTime() : 0;
        return dateA - dateB;
      });
  }, [colheitas, cicloId, setores, ciclo]);
  
  const cicloCor = cores?.find((cor: any) => cor.id === ciclo?.corFitaId);

  // Quando este ciclo fecha SOZINHO. A mesma conta do servidor
  // (shared/ciclo-fechamento.ts) — se as duas divergirem, a tela promete uma
  // data e o PUT recusa outra com 409 JANELA_ABERTA.
  const toleranciaDias = useToleranciaDias();
  const fechaEm = dataFechamentoAutomatico(
    cicloMangaras.map(m => m.dataPrevistaColheita),
    toleranciaDias,
  );
  const janelaAberta = janelaAindaAberta(fechaEm, hojeNaFazenda());

  // Calcular estatísticas de colheita
  const estatisticasColheita = useMemo(() => {
    if (cicloColheitas.length === 0 || cicloMangaras.length === 0) {
      return null;
    }
    return calcularEstatisticasColheita(cicloColheitas, cicloMangaras);
  }, [cicloColheitas, cicloMangaras]);

  const percentualRealizado = ciclo && ciclo.totalPlanejado > 0
    ? Math.round(((ciclo.totalColhido + ciclo.totalPerdido) / ciclo.totalPlanejado) * 100)
    : 0;

  const percentualColhido = ciclo && ciclo.totalPlanejado > 0
    ? Math.round((ciclo.totalColhido / ciclo.totalPlanejado) * 100)
    : 0;

  const percentualPerdido = ciclo && ciclo.totalPlanejado > 0
    ? Math.round((ciclo.totalPerdido / ciclo.totalPlanejado) * 100)
    : 0;

  const remanescente = ciclo 
    ? Math.max(0, ciclo.totalPlanejado - ciclo.totalColhido - ciclo.totalPerdido)
    : 0;

  // Get unique setores from cicloMangaras
  const setoresUnicos = cicloMangaras
    .filter((m, index, self) => 
      m.setor && self.findIndex(s => s.setor?.id === m.setor?.id) === index
    )
    .map(m => ({ id: m.setor!.id, nome: m.setor!.nome }));

  // Mutation para fechar ciclo manualmente
  const fecharCicloMutation = useMutation({
    mutationFn: async ({ distribuirPerdas, motivoPerdaId, forcar }: { distribuirPerdas: boolean, motivoPerdaId?: string, forcar: boolean }) => {
      const response = await fetch(`/api/ciclos/${cicloId}/fechar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          distribuirPerdas,
          forcar,
          ...(distribuirPerdas && motivoPerdaId ? { motivoPerdaId } : {}),
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Erro ao fechar ciclo");
      }
      
      return await response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos", cicloId] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/perdas"] });
      
      const mensagem = data.perdasDistribuidas 
        ? `O ciclo foi fechado e ${data.perdasDistribuidas.totalCachos} cachos foram distribuídos como perda entre ${data.perdasDistribuidas.setores} setor(es).`
        : "O ciclo foi fechado manualmente com sucesso.";
      
      toast({
        title: "Ciclo fechado",
        description: mensagem,
      });
      setIsFecharDialogOpen(false);
      setMotivoPerdaId("");
      setDistribuirPerdas(false);
      setForcarFechamento(false);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao fechar ciclo",
        description: error.message || "Não foi possível fechar o ciclo. Verifique se 100% do planejado foi justificado.",
        variant: "destructive",
      });
    },
  });

  // Mutation para reativar ciclo manualmente fechado
  const reativarCicloMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/ciclos/${cicloId}/reativar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Erro ao reativar ciclo");
      }
      
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos", cicloId] });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      
      toast({
        title: "Ciclo reativado",
        description: "O ciclo foi reativado com sucesso e está ativo novamente.",
      });
      setIsReativarDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao reativar ciclo",
        description: error.message || "Não foi possível reativar o ciclo.",
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = () => {
    if (!ciclo) return null;
    
    // Mangará que passou da previsão + tolerância CONFIGURADA (era `+ 15`).
    const temPrevisaoAtrasada = algumMangaraVencido(
      cicloMangaras.map(m => m.dataPrevistaColheita),
      toleranciaDias,
      hojeNaFazenda(),
    );

    // Se tem previsão atrasada → Atrasado
    if (temPrevisaoAtrasada) {
      return <Badge variant="destructive" className="bg-destructive/10 text-destructive border-destructive/20">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Atrasado
      </Badge>;
    }
    
    // Se está com boa realização → No Prazo
    if (percentualRealizado >= 90) {
      return <Badge variant="default" className="bg-primary/10 text-primary border-primary/20">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        No Prazo
      </Badge>;
    }
    
    // Caso contrário → Em Andamento
    return <Badge variant="secondary">
      <Activity className="h-3 w-3 mr-1" />
      Em Andamento
    </Badge>;
  };

  const getColheitaStatusBadge = (status: string) => {
    switch (status) {
      case "NO_PRAZO":
        return <Badge variant="default" className="bg-primary/10 text-primary border-primary/20">No Prazo</Badge>;
      case "ATRASADA":
        return <Badge variant="destructive" className="bg-destructive/10 text-destructive border-destructive/20">Atrasada</Badge>;
      case "ANTECIPADA":
        return <Badge variant="secondary" className="bg-secondary/80 text-secondary-foreground border-secondary">Antecipada</Badge>;
      case "SEM_PLANO":
        return <Badge variant="outline">Sem Plano</Badge>;
      default:
        return null;
    }
  };

  // Passou da data prevista + tolerância CONFIGURADA? (era `+ 15` cravado)
  const isPrevisaoAtrasada = (dataPrevista: string) =>
    algumMangaraVencido([dataPrevista], toleranciaDias, hojeNaFazenda());

  if (cicloLoading || mangarasLoading || colheitasLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-9 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!ciclo) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <XCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Ciclo não encontrado</h2>
        <p className="text-muted-foreground mb-6">O ciclo que você procura não existe ou foi removido.</p>
        <Button onClick={() => setLocation("/")} data-testid="button-back">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-3 md:gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => voltar("/cacho/ciclos")}
          className="w-fit"
          data-testid="button-back"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        
        <div className="flex flex-col gap-3 md:gap-4">
          <div className="space-y-2">
            <PageHeader
              titulo={`${cicloCor?.nome || "Sem cor"}${cicloVariedade ? ` — ${cicloVariedade.nome}` : ""}`}
            />
            <div className="flex flex-col gap-1 text-sm text-muted-foreground">
              <p>
                <span className="font-semibold">Código:</span> <span className="font-mono">{ciclo.codigo}</span>
              </p>
              <p>
                <span className="font-semibold">Corte dos Mangarás:</span> {ciclo.mesAno}
              </p>
            </div>
          </div>
          
          {/* Admin Actions - Ciclo Ativo */}
          {isAdmin && ciclo.status === "ATIVO" && (
            <div className="flex flex-wrap gap-2">
              {/* Registrar Perda - Só se houver remanescente */}
              {remanescente > 0 && setoresUnicos.length > 0 && (
                <RegistrarPerdaDialog 
                  cicloId={cicloId!}
                  remanescente={remanescente}
                  setores={setoresUnicos}
                />
              )}
              
              {/* Finalizar Ciclo - Aparece sempre quando houver algum progresso */}
              {percentualRealizado > 0 && (
                <Button
                  onClick={() => {
                    setIsFecharDialogOpen(true);
                    setForcarFechamento(false);
                    setDistribuirPerdas(false);
                    setMotivoPerdaId("");
                    refetchMotivos();
                  }}
                  variant="outline"
                  data-testid="button-fechar-ciclo"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Finalizar Ciclo
                </Button>
              )}
            </div>
          )}

          {/* Admin Actions - Ciclo Fechado Manualmente */}
          {isAdmin && ciclo.status === "FECHADO_MANUAL" && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setIsReativarDialogOpen(true)}
                variant="outline"
                data-testid="button-reativar-ciclo"
              >
                <Unlock className="h-4 w-4 mr-2" />
                Reativar Ciclo
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Informações do Ciclo */}
      <Card className="border-2">
        <CardContent className="pt-4 md:pt-6">
          <div className="grid gap-4 md:gap-6 md:grid-cols-3">
            {/* Cor da Fita e Variedade */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Cor da Fita</p>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-md border-2 shadow-sm"
                  style={{ 
                    backgroundColor: cicloCor?.hexColor || '#666',
                    borderColor: cicloCor?.hexColor || '#666'
                  }}
                  data-testid="ribbon-color-display"
                />
                <div>
                  <p className="text-lg md:text-xl font-bold capitalize">{cicloCor?.nome || 'N/A'}</p>
                  <p className="text-xs text-muted-foreground font-mono">{cicloCor?.hexColor}</p>
                </div>
              </div>
            </div>

            {/* Variedade */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Variedade</p>
              <div>
                <p className="text-lg md:text-xl font-bold uppercase">{cicloVariedade?.nome || 'N/A'}</p>
                <p className="text-xs text-muted-foreground">
                  Maturação prevista:{' '}
                  {ciclo && cicloVariedade ? (() => {
                    const meses: Record<string, number> = {
                      jan: 1, fev: 2, mar: 3, abr: 4, mai: 5, jun: 6,
                      jul: 7, ago: 8, set: 9, out: 10, nov: 11, dez: 12
                    };
                    const mesStr = ciclo.mesAno.substring(0, 3).toLowerCase();
                    const mesNum = meses[mesStr] || 0;
                    const semanas = cicloVariedade.semanasMaturacaoPorMes?.[mesNum.toString()] || 0;
                    return `${semanas} semanas`;
                  })() : 'N/A'}
                </p>
              </div>
            </div>

            {/* Mês do Corte */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Corte dos Mangarás</p>
              <div>
                {cicloMangaras.length > 0 ? (
                  (() => {
                    const datas = cicloMangaras.map(m => new Date(m.dataCorteMangara));
                    const minData = new Date(Math.min(...datas.map(d => d.getTime())));
                    const maxData = new Date(Math.max(...datas.map(d => d.getTime())));
                    
                    const minMes = minData.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
                    const maxMes = maxData.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
                    
                    const minWeek = getIsoWeek(minData);
                    const maxWeek = getIsoWeek(maxData);
                    const weekStr = minWeek.year === maxWeek.year && minWeek.week === maxWeek.week 
                      ? `S${minWeek.week}/${minWeek.year}`
                      : `S${minWeek.week}/${minWeek.year} - S${maxWeek.week}/${maxWeek.year}`;
                    
                    return (
                      <div className="flex flex-col gap-1">
                        <p className="text-base md:text-xl font-bold font-mono">
                          {weekStr}
                        </p>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
                          <p className="text-xs md:text-sm text-muted-foreground capitalize">
                            {minMes === maxMes ? minMes : `${minMes} - ${maxMes}`}
                          </p>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-xl font-bold text-muted-foreground">N/A</p>
                )}
              </div>
            </div>

            {/* Mês Previsto da Colheita */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Previsto de Colheita</p>
              <div>
                {cicloMangaras.length > 0 ? (
                  (() => {
                    const datas = cicloMangaras.map(m => new Date(m.dataPrevistaColheita));
                    const minData = new Date(Math.min(...datas.map(d => d.getTime())));
                    const maxData = new Date(Math.max(...datas.map(d => d.getTime())));
                    
                    const minMes = minData.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
                    const maxMes = maxData.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
                    
                    const minWeek = getIsoWeek(minData);
                    const maxWeek = getIsoWeek(maxData);
                    const weekStr = minWeek.year === maxWeek.year && minWeek.week === maxWeek.week 
                      ? `S${minWeek.week}/${minWeek.year}`
                      : `S${minWeek.week}/${minWeek.year} - S${maxWeek.week}/${maxWeek.year}`;
                    
                    return (
                      <div className="flex flex-col gap-1">
                        <p className="text-base md:text-xl font-bold font-mono">
                          {weekStr}
                        </p>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
                          <p className="text-xs md:text-sm text-muted-foreground capitalize">
                            {minMes === maxMes ? minMes : `${minMes} - ${maxMes}`}
                          </p>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-xl font-bold text-muted-foreground">N/A</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Overview Metrics */}
      <div className={`grid gap-3 grid-cols-2 md:gap-4 ${ciclo.status === 'FECHADO_MANUAL' && estatisticasColheita ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-2">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">Total Planejado</CardTitle>
            <Target className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ciclo.totalPlanejado}</div>
            <p className="text-xs text-muted-foreground mt-1">cachos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-2">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">Total Colhido</CardTitle>
            <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{ciclo.totalColhido}</div>
            <p className="text-xs text-muted-foreground mt-1">{percentualColhido}% do total</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-2">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">Falta Colher</CardTitle>
            <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Math.max(0, ciclo.totalPlanejado - ciclo.totalColhido - ciclo.totalPerdido)}</div>
            <p className="text-xs text-muted-foreground mt-1">cachos restantes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-2">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">% Realizado</CardTitle>
            <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{percentualRealizado}%</div>
            <p className="text-xs text-muted-foreground mt-1">{ciclo.totalColhido} colhidos + {ciclo.totalPerdido} perdidos</p>
          </CardContent>
        </Card>

        {/* Card de Média de Dias - Apenas quando ciclo estiver fechado */}
        {ciclo.status === 'FECHADO_MANUAL' && estatisticasColheita && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-2">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">Média de Dias</CardTitle>
              <Calendar className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Math.round(estatisticasColheita.mediana)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                previsão: {Math.round(estatisticasColheita.previsaoMedia)} dias
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      {/* Progress Bar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg md:text-xl font-semibold">
            Colheita da Fita {cicloCor?.nome || ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 md:space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progresso geral</span>
              <span className="font-mono font-semibold">{percentualRealizado}%</span>
            </div>
            <Progress value={percentualRealizado} className="h-3" />
          </div>
          
          <div className="grid gap-3 md:gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Cachos colhidos</span>
                <span className="font-mono font-semibold text-primary">{percentualColhido}%</span>
              </div>
              <Progress value={percentualColhido} className="h-2 bg-primary/20" />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Perdas registradas</span>
                <span className="font-mono font-semibold text-destructive">{percentualPerdido}%</span>
              </div>
              <Progress value={percentualPerdido} className="h-2 [&>div]:bg-destructive bg-destructive/20" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary (movido para antes de Colheitas) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg md:text-xl font-semibold">Resumo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 md:space-y-3">
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">Código do ciclo</span>
            <span className="font-mono font-semibold">{ciclo.codigo}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">Mês/Ano</span>
            <span className="font-mono font-semibold">{ciclo.mesAno}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">Total de Cachos Marcados</span>
            <span className="font-mono font-semibold">{cicloMangaras.reduce((sum, m) => sum + (m.quantidadeCachos || 0), 0)}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">Cachos Colhidos</span>
            <span className="font-mono font-semibold">{ciclo.totalColhido}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">Cachos Perdidos</span>
            <span className="font-mono font-semibold">{ciclo.totalPerdido}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">Faltam colher</span>
            <span className="font-mono font-semibold text-primary">
              {Math.max(0, ciclo.totalPlanejado - ciclo.totalColhido - ciclo.totalPerdido)} cachos
            </span>
          </div>

          {/* Estatísticas de colheita - apenas se houver dados */}
          {estatisticasColheita && (
            <>
              <div className="pt-3 mt-3 border-t">
                <h3 className="text-sm font-semibold mb-3">Estatísticas de Colheita</h3>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm text-muted-foreground">Previsão original</span>
                <span className="font-mono font-semibold">
                  {Math.round(estatisticasColheita.previsaoMedia)} dias / {estatisticasColheita.semanaPrevisao} semanas
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground">Realizado</span>
                <span className="font-mono font-semibold">
                  {Math.round(estatisticasColheita.realizadoMedia)} dias / {estatisticasColheita.semanaRealizado} semanas
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Mangarás do Ciclo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg md:text-xl font-semibold flex items-center gap-2">
            <Package className="h-4 w-4 md:h-5 md:w-5" />
            Mangarás Marcados ({cicloMangaras.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cicloMangaras.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum mangará marcado neste ciclo
            </p>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="md:hidden space-y-3">
                {cicloMangaras.map((mangara) => {
                  const atrasada = isPrevisaoAtrasada(mangara.dataPrevistaColheita);
                  return (
                    <Card 
                      key={mangara.id}
                      data-testid={`mangara-card-${mangara.id}`}
                    >
                      <CardContent className="pt-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Setor</p>
                            <p className="font-semibold">{mangara.setor?.nome || "-"}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Quantidade</p>
                            <p className="font-mono font-semibold">{mangara.quantidadeCachos}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Variedade</p>
                          <p className="text-sm">{mangara.setor?.variedade?.nome || "-"}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-sm text-muted-foreground">Data Corte</p>
                            <p className="text-sm font-mono font-bold">
                              S{getIsoWeek(new Date(mangara.dataCorteMangara)).week}/{getIsoWeek(new Date(mangara.dataCorteMangara)).year}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {new Date(mangara.dataCorteMangara + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Previsão Colheita</p>
                            <p className="text-sm font-mono font-bold">
                              S{getIsoWeek(new Date(mangara.dataPrevistaColheita)).week}/{getIsoWeek(new Date(mangara.dataPrevistaColheita)).year}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {new Date(mangara.dataPrevistaColheita + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Setor</TableHead>
                      <TableHead>Variedade</TableHead>
                      <TableHead>Data Corte</TableHead>
                      <TableHead>Previsão Colheita</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cicloMangaras.map((mangara) => {
                      const atrasada = isPrevisaoAtrasada(mangara.dataPrevistaColheita);
                      return (
                        <TableRow 
                          key={mangara.id} 
                          data-testid={`mangara-row-${mangara.id}`}
                        >
                          <TableCell className="font-medium">{mangara.setor?.nome || "-"}</TableCell>
                          <TableCell>{mangara.setor?.variedade?.nome || "-"}</TableCell>
                          <TableCell className="font-mono text-sm">
                            <div className="flex flex-col gap-0.5">
                              <span className="font-bold">S{getIsoWeek(new Date(mangara.dataCorteMangara)).week}/{getIsoWeek(new Date(mangara.dataCorteMangara)).year}</span>
                              <span className="text-xs text-muted-foreground">{new Date(mangara.dataCorteMangara + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            <div className="flex flex-col gap-0.5 whitespace-nowrap">
                              <span className="font-bold whitespace-nowrap">
                                S{getIsoWeek(new Date(mangara.dataPrevistaColheita)).week}/{getIsoWeek(new Date(mangara.dataPrevistaColheita)).year}
                              </span>
                              <span className="text-xs whitespace-nowrap text-muted-foreground">
                                {new Date(mangara.dataPrevistaColheita + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">{mangara.quantidadeCachos}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Colheitas do Ciclo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg md:text-xl font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5" />
            Colheitas Realizadas ({cicloColheitas.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cicloColheitas.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhuma colheita registrada neste ciclo ainda
            </p>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="md:hidden space-y-3">
                {cicloColheitas.map((colheita) => {
                  const diasPosCorte = getDiasPosCorte(colheita, cicloMangaras);
                  return (
                    <Card key={colheita.id} data-testid={`colheita-card-${colheita.id}`}>
                      <CardContent className="pt-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Setor</p>
                            <p className="font-semibold">{colheita.setor?.nome || "-"}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Quantidade</p>
                            <p className="font-mono font-semibold">{colheita.quantidadeCachos}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-sm text-muted-foreground">Data Colheita</p>
                            <p className="text-sm font-mono">
                              {new Date(colheita.dataColheita + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Dias pós corte</p>
                            <p className="text-sm font-mono font-semibold">
                              {diasPosCorte !== null ? `${diasPosCorte} dias` : "—"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t">
                          <p className="text-sm text-muted-foreground">Status</p>
                          <div className="flex flex-col gap-1 items-end">
                            {getColheitaStatusBadge(colheita.status)}
                            {colheita.foraTolerancia && (
                              <Badge variant="outline" className="text-xs border-atencao/30 text-atencao">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Fora da Tolerância
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Setor</TableHead>
                      <TableHead>Data Colheita</TableHead>
                      <TableHead className="text-right">Dias pós corte</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Observações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cicloColheitas.map((colheita) => {
                      const diasPosCorte = getDiasPosCorte(colheita, cicloMangaras);
                      return (
                        <TableRow key={colheita.id} data-testid={`colheita-row-${colheita.id}`}>
                          <TableCell className="font-medium">{colheita.setor?.nome || "-"}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {new Date(colheita.dataColheita + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold">
                            {diasPosCorte !== null ? `${diasPosCorte} dias` : "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono">{colheita.quantidadeCachos}</TableCell>
                          <TableCell>{getColheitaStatusBadge(colheita.status)}</TableCell>
                          <TableCell>
                            {colheita.foraTolerancia && (
                              <Badge variant="outline" className="text-xs border-atencao/30 text-atencao">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Fora da Tolerância
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Confirmação de Fechamento */}
      <AlertDialog open={isFecharDialogOpen} onOpenChange={setIsFecharDialogOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar Ciclo</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Você está prestes a finalizar o ciclo <span className="font-mono font-semibold">{ciclo.codigo}</span>.
                  Após o fechamento, não será possível adicionar novas colheitas ou perdas a este ciclo.
                </p>

                {/* Fechar antes de previsto + tolerância foi o que, em 09/05/2026,
                    jogou as colheitas tardias de nov/25–jan/26 na fita seguinte da
                    mesma cor. O aviso aparece na hora em que a decisão é tomada —
                    e não bloqueia: o checkbox libera. */}
                {janelaAberta && (
                  <div className="space-y-3 p-4 border border-atencao/30 rounded-md bg-atencao-suave">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-atencao mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium">A janela de colheita ainda está aberta.</p>
                        <p className="text-muted-foreground mt-1">
                          Este ciclo fecha sozinho em{" "}
                          {/* Data civil pela STRING — `new Date(iso)` mostraria o dia anterior a oeste de UTC. */}
                          <span className="font-semibold">{fechaEm!.split("-").reverse().join("/")}</span>. Fechar
                          agora faz as colheitas que ainda vierem caírem na próxima fita da mesma cor.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="forcar-fechamento"
                        checked={forcarFechamento}
                        onCheckedChange={(checked) => setForcarFechamento(checked === true)}
                        data-testid="checkbox-forcar-fechamento"
                      />
                      <Label htmlFor="forcar-fechamento" className="text-sm font-medium cursor-pointer">
                        Fechar mesmo assim
                      </Label>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-sm bg-muted/50 p-3 rounded-md">
                  <div>
                    <span className="text-muted-foreground">Total Planejado:</span>
                    <p className="font-semibold">{ciclo.totalPlanejado} cachos</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Colhido:</span>
                    <p className="font-semibold text-primary">{ciclo.totalColhido} cachos ({percentualColhido}%)</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Perdido:</span>
                    <p className="font-semibold text-destructive">{ciclo.totalPerdido} cachos ({percentualPerdido}%)</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Remanescente:</span>
                    <p className="font-semibold text-atencao">{remanescente} cachos</p>
                  </div>
                </div>

                {remanescente > 0 && (
                  <div className="space-y-3 p-4 border rounded-md bg-atencao-suave">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="distribuir-perdas"
                        checked={distribuirPerdas}
                        onCheckedChange={(checked) => setDistribuirPerdas(checked === true)}
                        data-testid="checkbox-distribuir-perdas"
                      />
                      <div className="flex-1">
                        <Label
                          htmlFor="distribuir-perdas"
                          className="text-sm font-medium cursor-pointer"
                        >
                          Distribuir os {remanescente} cachos restantes como perda?
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          As perdas serão distribuídas proporcionalmente entre os setores do ciclo.
                        </p>
                      </div>
                    </div>

                    {distribuirPerdas && (
                      <div className="space-y-2 pl-9">
                        <Label htmlFor="motivo-perda" className="text-sm">
                          Motivo da Perda *
                        </Label>
                        {(!motivosPerdas || motivosPerdas.length === 0) ? (
                          <div className="p-3 rounded-md bg-erro-suave border border-erro/30">
                            <p className="text-sm text-erro">
                              Nenhum motivo de perda disponível. Por favor, crie motivos de perda em Configurações antes de finalizar o ciclo.
                            </p>
                          </div>
                        ) : (
                          <Select
                            value={motivoPerdaId}
                            onValueChange={setMotivoPerdaId}
                          >
                            <SelectTrigger id="motivo-perda" data-testid="select-motivo-perda">
                              <SelectValue placeholder="Selecione um motivo" />
                            </SelectTrigger>
                            <SelectContent>
                              {motivosPerdas.map((motivo) => (
                                <SelectItem key={motivo.id} value={motivo.id}>
                                  {motivo.descricao || motivo.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {percentualRealizado >= 100 && (
                  <p className="text-sm font-medium text-primary">
                    ✓ O ciclo está 100% completo e pronto para ser finalizado.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancelar-fechar">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (distribuirPerdas && !motivoPerdaId) {
                  toast({
                    title: "Motivo de perda necessário",
                    description: "Por favor, selecione um motivo para a perda.",
                    variant: "destructive",
                  });
                  return;
                }
                fecharCicloMutation.mutate({ distribuirPerdas, motivoPerdaId, forcar: forcarFechamento });
              }}
              disabled={
                fecharCicloMutation.isPending
                || (remanescente > 0 && !distribuirPerdas)
                || (janelaAberta && !forcarFechamento)
              }
              data-testid="button-confirmar-fechar"
            >
              {fecharCicloMutation.isPending ? "Finalizando..." : "Finalizar Ciclo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Confirmação de Reativação */}
      <AlertDialog open={isReativarDialogOpen} onOpenChange={setIsReativarDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Reativar Ciclo</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Você está prestes a reativar o ciclo <span className="font-mono font-semibold">{ciclo.codigo}</span>.
                </p>
                <p>
                  Após a reativação, será possível adicionar novas colheitas e perdas a este ciclo.
                </p>
                <div className="p-3 bg-atencao-suave border border-atencao/30 rounded-md">
                  <p className="text-sm text-atencao">
                    <strong>Atenção:</strong> As perdas distribuídas automaticamente no fechamento anterior não serão removidas. 
                    Você precisará ajustar manualmente se necessário.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancelar-reativar">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => reativarCicloMutation.mutate()}
              disabled={reativarCicloMutation.isPending}
              data-testid="button-confirmar-reativar"
            >
              {reativarCicloMutation.isPending ? "Reativando..." : "Reativar Ciclo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
